import { useEffect, useRef, useState } from "react";

const CHAT_STORAGE_PREFIX = "pcm_chat_";

const CopyMessageButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      className={`pcm-chat-copy${copied ? " pcm-chat-copy--copied" : ""}`}
      onClick={handleCopy}
      aria-label="Copy message"
      data-testid="chat-message-copy"
    >
      {copied ? "Copied!" : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </button>
  );
};

const loadHistory = (projectId) => {
  try {
    return JSON.parse(
      localStorage.getItem(`${CHAT_STORAGE_PREFIX}${projectId}`) || "[]",
    );
  } catch {
    return [];
  }
};

const saveHistory = (projectId, messages) => {
  localStorage.setItem(
    `${CHAT_STORAGE_PREFIX}${projectId}`,
    JSON.stringify(messages),
  );
};

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const ChatPanel = ({ projectId, projectName, brief, getSessionSettings, backendUrl }) => {
  const [messages, setMessages] = useState(() => loadHistory(projectId));
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    setMessages(loadHistory(projectId));
    setError("");
    setInput("");
  }, [projectId]);

  useEffect(() => {
    saveHistory(projectId, messages);
  }, [projectId, messages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const activeSettings = getSessionSettings();
    const provider = activeSettings.activeProvider;
    const apiKey = activeSettings.keys?.[provider]?.trim();
    if (!apiKey) {
      setError("Please add an API key in Settings before chatting.");
      return;
    }

    const userMessage = { id: newId(), role: "user", content: text };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput("");
    setError("");
    setIsLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: nextMessages.map(({ role, content }) => ({ role, content })),
          api_key: apiKey,
          provider,
          system_prompt: brief || "",
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || data?.error?.message || "Failed to get a response.");
      }
      setMessages((prev) => [
        ...prev,
        { id: newId(), role: "assistant", content: data.reply },
      ]);
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    setMessages([]);
    setError("");
  };

  const contextPreview = brief
    ? brief.slice(0, 140) + (brief.length > 140 ? "…" : "")
    : "No context brief set for this project.";

  return (
    <div className="pcm-chat" data-testid="chat-panel">
      <div className="pcm-chat-context" data-testid="chat-context-indicator">
        <span className="pcm-chat-context-label">Context:</span>
        <span className="pcm-chat-context-value">{contextPreview}</span>
      </div>

      <div className="pcm-chat-messages" data-testid="chat-messages">
        {messages.length === 0 && (
          <p className="pcm-chat-empty" data-testid="chat-empty">
            Ask anything about <strong>{projectName}</strong>. Your context brief is loaded as the system prompt.
          </p>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`pcm-chat-message pcm-chat-message--${msg.role}`}
            data-testid={`chat-message-${msg.role}`}
          >
            <span className="pcm-chat-message-role">
              {msg.role === "user" ? "You" : "AI"}
            </span>
            <div className="pcm-chat-message-bubble">
              <p className="pcm-chat-message-content">{msg.content}</p>
              {msg.role === "assistant" && <CopyMessageButton text={msg.content} />}
            </div>
          </div>
        ))}
        {isLoading && (
          <div
            className="pcm-chat-message pcm-chat-message--assistant"
            data-testid="chat-loading"
          >
            <span className="pcm-chat-message-role">AI</span>
            <p className="pcm-chat-message-content">
              <span className="pcm-chat-typing">···</span>
            </p>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {error && (
        <p className="pcm-chat-error" data-testid="chat-error">
          {error}
        </p>
      )}

      <div className="pcm-chat-input-row" data-testid="chat-input-row">
        <textarea
          className="pcm-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about this project… (Enter to send, Shift+Enter for new line)"
          rows={2}
          disabled={isLoading}
          data-testid="chat-input"
        />
        <div className="pcm-chat-actions">
          <button
            type="button"
            className="pcm-button pcm-button-primary pcm-chat-send"
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            data-testid="chat-send-button"
          >
            {isLoading ? "Thinking…" : "Send"}
          </button>
          {messages.length > 0 && (
            <button
              type="button"
              className="pcm-chat-clear"
              onClick={handleClear}
              data-testid="chat-clear-button"
            >
              Clear chat
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
